import { errorSchema } from "@app/shared";
import { useEffect, useRef, useState } from "react";
import { ApiError } from "../lib/api";
import {
  type Reglages,
  type RenduReponse,
  type RenduRequete,
  renduReponseSchema,
} from "./contract";

/**
 * L'envoi d'un rendu et son rythme : une nouvelle image part tout de suite, un reglage 600 ms
 * apres le dernier changement, et **une seule requete est en vol** a la fois (spec du
 * 21 septembre 2026, section 1 ; plan #145).
 *
 * Pourquoi une seule : c'est `products` qui fait passer les rendus un par un (son verrou), et
 * une rafale de requetes n'irait pas plus vite. Un reglage change pendant un calcul attend
 * la fin de celui-ci, dans une file d'une place : seule la derniere demande part, les
 * intermediaires n'interessent personne.
 */

export const ROUTE_RENDU = "/api/silhouettes/rendu";

/** Le temps laisse a un reglage pour se poser avant de partir. */
export const DELAI_REGLAGE_MS = 600;

/**
 * Au-dela, la page dit que c'est plus long que d'habitude : l'image est detaillee, ou un autre
 * rendu passe avant. 10 s : le rendu tourne dans un processus a part cote `products`, qui
 * ajoute environ 0,6 s a chacun, et une image detaillee prend deja plusieurs secondes.
 */
export const DELAI_LONG_MS = 10_000;

/**
 * `POST /api/silhouettes/rendu`, comme `apiGet` fait ses lectures : une `ApiError` avec le code
 * et le message du backend, `http_error` et le statut quand le corps n'est pas du JSON (une
 * coupure du frontal nginx), `contract_broken` pour une reponse hors contrat.
 */
export async function envoyerRendu(
  requete: RenduRequete,
  signal?: AbortSignal,
): Promise<RenduReponse> {
  const response = await fetch(ROUTE_RENDU, {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify(requete),
    ...(signal ? { signal } : {}),
  });
  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = errorSchema.safeParse(payload);
    throw new ApiError(
      parsed.success ? parsed.data.error.code : "http_error",
      parsed.success ? parsed.data.error.message : `Erreur HTTP ${response.status}`,
      response.status,
    );
  }

  const result = renduReponseSchema.safeParse(payload);
  if (!result.success) {
    throw new ApiError("contract_broken", "Reponse hors contrat", response.status);
  }
  return result.data;
}

export type EtatRendu = "vide" | "attente" | "calcul" | "pret" | "erreur";

/** L'image telle qu'elle part : son identite compte, une nouvelle image part tout de suite. */
export type ImageEnvoi = { base64: string; nomFichier: string };

export type Rendu = {
  /**
   * `vide` sans image ; `attente` quand l'image est la mais que rien n'est montre pour elle
   * (un reglage invalide : rien n'est demande), apercu vide et sans « a jour » ; `calcul`
   * tant qu'une demande attend ou est en vol ; `pret` quand la reponse affichee repond a la
   * derniere demande ; `erreur` quand celle-ci a echoue.
   */
  etat: EtatRendu;
  /**
   * La derniere reponse reussie **pour l'image courante** : elle reste affichee pendant le
   * calcul suivant, mais pas pour une autre image (son rapport, ses noms de fichiers et son
   * fond ne diraient rien de la nouvelle).
   */
  reponse: RenduReponse | null;
  /** L'echec de la derniere demande, et d'elle seule : jamais celui d'une autre image. */
  erreur: ApiError | null;
  /**
   * Les couleurs **de la demande qui a echoue**, jamais celles de la carte. Un message
   * d'erreur decrit une requete passee : relire la saisie le ferait changer de texte sous
   * les doigts de Nicolas, et repondre a son clic sur le lien par le message qu'il venait
   * de faire disparaitre (relecture de l'issue #186).
   *
   * **Ne se lit que sous `erreur`** : hors de ce garde, son `null` dit seulement qu'il n'y a
   * pas d'erreur pour l'image courante. Sous le garde, il dit que la demande refusee n'avait
   * pas de couleurs.
   */
  couleursDeLErreur: Reglages["couleurs"];
  /** Vrai apres 10 s de calcul. */
  long: boolean;
  /** Renvoie la derniere demande, telle quelle. */
  relancer: () => void;
};

/** L'etat que tient le moteur : `attente` n'existe que vu de l'image courante. */
type EtatMoteur = Exclude<EtatRendu, "attente">;

type Demande = { id: number; requete: RenduRequete; image: ImageEnvoi };

/** Une reponse, et l'image qui l'a produite. */
type ReponseDeLImage = { reponse: RenduReponse; image: ImageEnvoi };

/** Un echec, et ce qu'il faut savoir de la demande qui a echoue pour en parler. */
type ErreurDeLImage = {
  erreur: ApiError;
  image: ImageEnvoi;
  couleurs: Reglages["couleurs"];
};

type Afficheurs = {
  setEtat: (etat: EtatMoteur) => void;
  setReponse: (reponse: ReponseDeLImage) => void;
  setErreur: (erreur: ErreurDeLImage | null) => void;
  setLong: (long: boolean) => void;
};

/**
 * Le moteur du rythme, hors de React : des minuteurs, une requete en vol et une file d'une
 * place, qui ne font que pousser leur etat dans les `useState` du crochet. Il est cree une
 * fois par montage.
 */
function creerMoteur(afficher: Afficheurs) {
  let actif = true;
  let compteur = 0;
  /** La derniere demande faite : en vol, ou en file derriere celle qui l'est. */
  let derniere: Demande | null = null;
  /** Ce qu'est devenue la derniere demande. */
  let issue: "attente" | "pret" | "erreur" = "attente";
  let enVol: { demande: Demande; controleur: AbortController } | null = null;
  /** La demande qui attend la fin de celle en vol. Une seule : la plus recente. */
  let enFile: Demande | null = null;
  let minuteur: ReturnType<typeof setTimeout> | null = null;
  let minuteurLong: ReturnType<typeof setTimeout> | null = null;

  const rafraichir = (): void => {
    if (minuteur) afficher.setEtat("calcul");
    // Rien de demande pour l'image affichee : une demande en vol pour une autre ne compte pas.
    else if (!derniere) afficher.setEtat("vide");
    // La derniere demande est en vol, ou en file derriere celle qui l'est.
    else if (enVol) afficher.setEtat("calcul");
    else afficher.setEtat(issue === "erreur" ? "erreur" : issue === "pret" ? "pret" : "calcul");
  };

  const annulerMinuteur = (): void => {
    if (minuteur) clearTimeout(minuteur);
    minuteur = null;
  };

  const envoyer = (demande: Demande): void => {
    const controleur = new AbortController();
    enVol = { demande, controleur };
    afficher.setLong(false);
    minuteurLong = setTimeout(() => afficher.setLong(true), DELAI_LONG_MS);
    rafraichir();
    envoyerRendu(demande.requete, controleur.signal)
      .then(
        (reponse) => {
          if (!actif) return;
          // Meme une reponse depassee s'affiche, grisee : elle est plus recente que la
          // precedente. Elle ne rend la page « prete » que si elle repond a la derniere.
          afficher.setReponse({ reponse, image: demande.image });
          if (derniere?.id === demande.id) {
            issue = "pret";
            afficher.setErreur(null);
          }
        },
        (erreur: unknown) => {
          if (!actif || controleur.signal.aborted) return;
          if (derniere?.id !== demande.id) return;
          issue = "erreur";
          afficher.setErreur({
            erreur:
              erreur instanceof ApiError
                ? erreur
                : new ApiError("reseau", "Le calcul est injoignable pour l'instant.", 0),
            image: demande.image,
            couleurs: demande.requete.couleurs,
          });
        },
      )
      .finally(() => {
        if (minuteurLong) clearTimeout(minuteurLong);
        minuteurLong = null;
        if (!actif || enVol?.demande.id !== demande.id) return;
        enVol = null;
        afficher.setLong(false);
        // La demande qui attendait la fin de celle-ci part maintenant, et elle seule.
        const suivante = enFile;
        enFile = null;
        if (suivante) envoyer(suivante);
        else rafraichir();
      });
  };

  const demander = (requete: RenduRequete, image: ImageEnvoi): void => {
    compteur += 1;
    derniere = { id: compteur, requete, image };
    issue = "attente";
    // L'echec garde jusqu'ici etait celui d'une demande precedente : il ne dit rien de celle-ci.
    afficher.setErreur(null);
    if (enVol) {
      enFile = derniere;
      rafraichir();
    } else {
      envoyer(derniere);
    }
  };

  return {
    /** Demande un rendu, tout de suite ou apres `delai`, en remplacant la demande en attente. */
    planifier(requete: RenduRequete, image: ImageEnvoi, delai: number): void {
      annulerMinuteur();
      if (delai <= 0) {
        demander(requete, image);
        return;
      }
      minuteur = setTimeout(() => {
        minuteur = null;
        demander(requete, image);
      }, delai);
      rafraichir();
    },
    /**
     * Un reglage invalide : rien ne part. Le minuteur est oublie, et la demande en file aussi :
     * la derniere redevient celle qui est en vol, et son issue fera l'etat de la page, **si
     * elle porte `image`, l'image affichee**. Une demande pour une autre image (Nicolas en a
     * choisi une nouvelle, puis a vide Hauteur) ne parle plus pour la page : ni son refus, ni
     * sa reponse, ni Reessayer ne doivent venir d'elle. Rien n'est alors demande pour l'image
     * affichee, et la page attend un reglage valide.
     */
    suspendre(image: ImageEnvoi): void {
      annulerMinuteur();
      if (enFile) {
        enFile = null;
        derniere = enVol?.demande ?? null;
        issue = "attente";
      }
      if (derniere && derniere.image !== image) {
        derniere = null;
        issue = "attente";
        afficher.setErreur(null);
      }
      rafraichir();
    },
    relancer(): void {
      if (!actif || enVol || !derniere) return;
      issue = "attente";
      envoyer(derniere);
    },
    reprendre(): void {
      actif = true;
    },
    /** Au demontage : la requete en vol est annulee, les minuteurs aussi. */
    arreter(): void {
      actif = false;
      annulerMinuteur();
      if (minuteurLong) clearTimeout(minuteurLong);
      minuteurLong = null;
      enVol?.controleur.abort();
      enVol = null;
      enFile = null;
    },
  };
}

/**
 * L'etat que la page montre pour l'image affichee. Rien a montrer pour elle (rien de demande,
 * ou une reponse et un echec qui portent sur une autre image) : la page attend, apercu vide,
 * ni « a jour » ni erreur. Le moteur ne voit une nouvelle image qu'a son effet, un rendu plus
 * tard : d'ici la, ce qu'il dit porte encore sur la precedente.
 */
function etatPourLImage(
  etat: EtatMoteur,
  image: ImageEnvoi | null,
  reponse: RenduReponse | null,
  erreur: ApiError | null,
): EtatRendu {
  if (image === null) return "vide";
  if (etat === "vide") return "attente";
  if (etat === "pret" && reponse === null) return "attente";
  if (etat === "erreur" && erreur === null) return "attente";
  return etat;
}

/**
 * Le rendu de la page : `image` part tout de suite, un changement de `reglages` 600 ms apres
 * le dernier, et rien ne part tant que `reglagesValides` est faux.
 */
export function useRendu(
  image: ImageEnvoi | null,
  reglages: Reglages,
  reglagesValides: boolean,
): Rendu {
  const [etat, setEtat] = useState<EtatMoteur>("vide");
  const [reponse, setReponse] = useState<ReponseDeLImage | null>(null);
  const [erreur, setErreur] = useState<ErreurDeLImage | null>(null);
  const [long, setLong] = useState(false);
  const [moteur] = useState(() => creerMoteur({ setEtat, setReponse, setErreur, setLong }));
  const imageVue = useRef<ImageEnvoi | null>(null);
  // La cle et non l'objet : la page peut rebatir ses reglages a chaque rendu sans rien envoyer.
  const cleReglages = JSON.stringify(reglages);

  useEffect(() => {
    moteur.reprendre();
    return () => moteur.arreter();
  }, [moteur]);

  useEffect(() => {
    if (!image) return;
    if (!reglagesValides) {
      moteur.suspendre(image);
      return;
    }
    const requete: RenduRequete = {
      ...(JSON.parse(cleReglages) as Reglages),
      image: image.base64,
      nom_fichier: image.nomFichier,
    };
    const nouvelle = imageVue.current !== image;
    imageVue.current = image;
    moteur.planifier(requete, image, nouvelle ? 0 : DELAI_REGLAGE_MS);
  }, [image, cleReglages, reglagesValides, moteur]);

  // Une reponse, comme un echec, ne vaut que pour l'image de sa demande.
  const reponseVue = reponse && reponse.image === image ? reponse.reponse : null;
  const erreurVue = erreur && erreur.image === image ? erreur : null;
  return {
    etat: etatPourLImage(etat, image, reponseVue, erreurVue?.erreur ?? null),
    reponse: reponseVue,
    erreur: erreurVue?.erreur ?? null,
    couleursDeLErreur: erreurVue?.couleurs ?? null,
    long,
    relancer: moteur.relancer,
  };
}
